import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const NotificationContext = createContext({});

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['common', 'errors']);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: "error",
      title: t('common:messages.error'),
      message: t('common:messages.somethingWentWrong'),
      duration: 5000,
      ...notification,
    };

    setNotifications((prev) => [...prev, newNotification]);

    if (newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const showError = useCallback(
    (message, title = null, duration = 5000) => {
      // Check if message contains network error (case insensitive). /uni-admin
      // has its own backend/auth and its own /uni-admin/network-error page, so
      // a transient failure there must not hijack navigation away from a login
      // or auth-loading state that hasn't resolved yet -- fall through to a
      // normal toast instead of going silent.
      const isNetworkErrorMessage = message.toLowerCase().includes("network error");
      const isUniAdminPath = location.pathname.startsWith("/uni-admin");
      if (isNetworkErrorMessage && !isUniAdminPath) {
        // Don't show notification, redirect to network error page
        if (location.pathname !== "/network-error") {
          navigate("/network-error", { replace: true });
        }
        return null;
      }

      return addNotification({
        type: "error",
        title: title || t('common:messages.error'),
        message,
        duration,
      });
    },
    [addNotification, navigate, location.pathname, t]
  );

  const showSuccess = useCallback(
    (message, title = null, duration = 4000) => {
      return addNotification({
        type: "success",
        title: title || t('common:messages.success'),
        message,
        duration,
      });
    },
    [addNotification, t]
  );

  const showWarning = useCallback(
    (message, title = null, duration = 4000) => {
      return addNotification({
        type: "warning",
        title: title || t('common:messages.warning'),
        message,
        duration,
      });
    },
    [addNotification, t]
  );

  const showInfo = useCallback(
    (message, title = null, duration = 4000) => {
      return addNotification({
        type: "info",
        title: title || t('common:messages.info'),
        message,
        duration,
      });
    },
    [addNotification, t]
  );

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showError,
    showSuccess,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
